import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
} from "@tanstack/react-router";
import { useLocation } from "@tanstack/react-router";

import {
  readStoredLanguage,
  useActiveLanguageIsUnseeded,
} from "../lib/activeLanguage";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";

/**
 * `/practice` — fetches the next exercise from the scheduler and redirects
 * to `/practice/:id` before rendering. Child routes render via <Outlet />.
 *
 * When the scheduler is slow (Bellman rollouts over ~1.8k exercises
 * currently take 5-15s) or no exercise is available, PracticeLayout
 * renders a proper landing — without this, the bare /practice path
 * would Outlet to nothing and the user sees an empty page.
 */
export const Route = createFileRoute("/practice")({
  beforeLoad: async ({ location, context }) => {
    // Auth gate fires first — if not signed in, redirect to /login with
    // returnTo preserved so the user lands back on the practice path
    // they were aiming for. requireAuth throws a redirect on miss.
    await requireAuth(context.queryClient, location.href);
    // TanStack file-based routing makes `practice.$exerciseId.tsx` a CHILD of
    // `practice.tsx`, so this beforeLoad fires for both /practice and
    // /practice/$exerciseId. Only redirect on the exact /practice path,
    // otherwise you get an infinite redirect loop.
    if (location.pathname.replace(/\/$/, "") !== "/practice") return;

    // Use the same base URL as the typed api client (relative /api in
    // dev, absolute api.lingua.cardona-apps.com in prod). Without this,
    // production hits the web service and gets the SPA HTML back.
    const apiBase = import.meta.env.VITE_API_BASE_URL || "/api";
    // beforeLoad runs outside any React component — read the active
    // language straight from localStorage via the shared helper so this
    // redirect lines up with whatever the LanguageSwitcher last picked.
    const language = readStoredLanguage();
    let id: number | undefined;
    let data: { exercise?: { id?: number } } | undefined;
    try {
      const res = await fetch(
        `${apiBase}/exercises/next?language=${encodeURIComponent(language)}`,
        // credentials: "include" is required cross-origin so the session
        // cookie travels — lib/api.ts does this for openapi-fetch calls,
        // but this raw fetch (used so beforeLoad doesn't depend on the
        // generated client) has to opt in by hand.
        { credentials: "include" },
      );
      if (!res.ok) return;
      data = (await res.json()) as { exercise?: { id?: number } };
      id = data?.exercise?.id;
    } catch {
      // Network/parse failure: fall through to the landing component,
      // which shows a retry button.
      return;
    }
    if (typeof id === "number") {
      // Pre-populate React Query so the destination route's queryFn
      // hits the cache instead of round-tripping /exercises/{id}
      // again — the `next` response already carries the full
      // exercise + scheduler rationale.
      context.queryClient.setQueryData(["exercise", String(id)], data);
      // Throw outside the try/catch: TanStack Router's redirect() now
      // returns a Response object, not a plain { isRedirect: true }, so
      // a generic catch would silently swallow the redirect and the
      // user would land on the empty fallback.
      throw redirect({
        to: "/practice/$exerciseId",
        params: { exerciseId: String(id) },
        replace: true,
      });
    }
  },
  component: PracticeLayout,
  pendingComponent: PracticeLoading,
  // Show the pending UI almost immediately so a 15s slow next call
  // doesn't look like the page is broken.
  pendingMs: 250,
});

function PracticeLayout() {
  const { pathname } = useLocation();
  const unseeded = useActiveLanguageIsUnseeded();
  // If we landed on the bare /practice path it means beforeLoad
  // returned without redirecting — either because no exercise was
  // available (queue empty / scheduler errored) or because the active
  // language is one we haven't seeded yet. The unseeded case gets a
  // dedicated landing that names the language and offers a clean
  // "Back to German" reset; the generic empty case keeps the
  // try-again affordance.
  if (pathname.replace(/\/$/, "") === "/practice") {
    if (unseeded) return <UnseededLanguageLanding />;
    return <PracticeLanding variant="empty" />;
  }
  return <Outlet />;
}

function PracticeLoading() {
  return <PracticeLanding variant="loading" />;
}

function PracticeLanding({ variant }: { variant: "loading" | "empty" }) {
  return (
    <section className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
      <div className="eyebrow mb-3">Practice</div>
      <h1 className="font-serif text-3xl text-ink-900 mb-4 tracking-tight">
        {variant === "loading"
          ? "Finding your next exercise"
          : "No exercise available"}
      </h1>
      {/* A slow heartbeat hairline under the h1 — gives the loading
          state a sense of "the system is thinking" without resorting
          to a spinner glyph. The empty variant keeps a static rule
          so the visual rhythm matches across both states. */}
      <div className="mx-auto mb-8 h-px w-24 overflow-hidden bg-ink-100">
        {variant === "loading" && (
          <div className="h-full w-1/2 bg-ink-700 origin-left animate-thinking-rule" />
        )}
      </div>
      <p className="text-ink-500 mb-8 max-w-md mx-auto">
        {variant === "loading"
          ? "The scheduler is picking the most useful drill for you. This can take a few seconds when the queue is large."
          : "Either the scheduler couldn't pick anything just now, or your queue is empty. Try again, or browse a specific skill."}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        {variant === "empty" && (
          <Link
            to="/practice"
            className="bg-accent px-5 py-3 text-accent-fg hover:bg-accent-hover transition-colors"
          >
            Try again
          </Link>
        )}
        <Link
          to="/skills"
          className="border border-ink-200 px-5 py-3 text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors"
        >
          Browse skills
        </Link>
      </div>
    </section>
  );
}
